import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button } from 'antd';
import { IUpdateConstructionSiteApiRequest } from 'Data/interfaces/Obra/IUpdateConstructionSiteApiRequest';
import { useQueryUpdateApiObra } from 'Hooks/obras/useQueryUpdateApiObra';
import { Icon } from 'Components/UI';
import { ApisEnum, ApisEnumLabel } from 'Data/enums/Apis';
import coordlyIcon from 'Static/images/logo-coordly-collapsed.png';
import googleDriveIcon from 'Static/images/google-drive-icon.png';
import dropboxIcon from 'Static/images/dropbox-icon.png';
import oneDriveIcon from 'Static/images/onedrive-icon.png';
import {
  TableWrapper,
  TableHeader,
  TableHeaderLeft,
  SkeletonCheckbox,
  SkeletonRows,
  SkeletonRow,
  SkeletonIcon,
  SkeletonText,
  BoxCard,
  IconsHeader,
  AppIconWrapper,
  WarningDot,
  DashedLine,
  Title,
  Description,
  EmailBadge,
  ActionsRow,
} from './styles';

interface ReconnectIntegrationBoxProps {
  integrationEmail: string;
  showReconnectButton: boolean;
  csId: number;
  api: number;
}

const getServiceIcon = (api: number): string => {
  switch (api) {
    case ApisEnum.GoogleDrive:
      return googleDriveIcon;
    case ApisEnum.Dropbox:
      return dropboxIcon;
    case ApisEnum.OneDrive:
      return oneDriveIcon;
    default:
      return googleDriveIcon;
  }
};

const ReconnectIntegrationBox: React.FC<ReconnectIntegrationBoxProps> = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation('disciplinas');
  const { updateApiObra } = useQueryUpdateApiObra({
    dispatch,
    navigate
  });

  const [isRedirecting, setIsRedirecting] = useState(false);
  const serviceName = ApisEnumLabel[props.api] || 'Google Drive';
  const serviceIcon = getServiceIcon(props.api);

  const handleReconnection = () => {
    setIsRedirecting(true);

    const request: IUpdateConstructionSiteApiRequest = {
      CsId: props.csId,
      Api: props.api,
      ExistingConstructionSiteTokenId: 0,
      isRedirect: true,
      sameAccountRequired: true,
    };
    updateApiObra(request);
  };

  return (
    <TableWrapper>
      <TableHeader>
        <TableHeaderLeft>
          <SkeletonCheckbox />
        </TableHeaderLeft>
      </TableHeader>
      <SkeletonRows>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((index) => (
          <SkeletonRow key={index}>
            <SkeletonCheckbox />
            <SkeletonIcon />
            <SkeletonText />
          </SkeletonRow>
        ))}
      </SkeletonRows>
      <BoxCard>
        <Title>
          {t('reconnectBox.title', { serviceName })}
        </Title>
        <IconsHeader>
          <AppIconWrapper>
            <img src={coordlyIcon} alt='Coordly' width={40} height={40} />
          </AppIconWrapper>
          <DashedLine />
          <WarningDot>
            <Icon icon='aviso' customColor='#FF4D4F' customSize={16} />
          </WarningDot>
          <DashedLine />
          <AppIconWrapper>
            <img src={serviceIcon} alt={serviceName} width={40} height={40} />
          </AppIconWrapper>
        </IconsHeader>

        {props.showReconnectButton ? (
          <>
            <Description>
              {t('reconnectBox.descriptionConnected')}
            </Description>
            <Description secondary>
              {t('reconnectBox.instructionConnected')}
            </Description>
            <EmailBadge>
              <Icon icon='email' customSize={14} customColor='#595959' />
              <span>{props.integrationEmail}</span>
            </EmailBadge>
            <ActionsRow>
              <Button
                type='primary'
                onClick={handleReconnection}
                loading={isRedirecting}
                icon={<Icon icon='link' customSize={14} customColor='#ffffff' />}
              >
                {t('reconnectBox.reconnectButton', { serviceName })}
              </Button>
              <Button
                type='default'
                icon={<Icon icon='menuAjuda' customSize={14} />}
              >
                {t('reconnectBox.helpButton')}
              </Button>
            </ActionsRow>
          </>
        ) : (
          <>
            <Description>
              {t('reconnectBox.descriptionAdmin')}
            </Description>
            <Description secondary>
              <strong>{t('reconnectBox.instructionAdmin')}</strong>
            </Description>
          </>
        )}
      </BoxCard>
    </TableWrapper>
  );
};

export default ReconnectIntegrationBox;